// src/components/booking/BookingSuccessPage.js
import React, { useEffect, useState } from 'react';
import { 
  CheckCircle, 
  Calendar, 
  ArrowRight, 
  Home, 
  Car, 
  Clock, 
  Receipt, 
  User, 
  Download,
  ChevronDown,
  ChevronUp,
  Mail
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import emailService from '../../services/emailService';

const BookingSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get query parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const payment_intent = queryParams.get('payment_intent');
  const booking_id = queryParams.get('booking_id');
  
  const { user } = useAuth();
  const [countdown, setCountdown] = useState(10);
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullReceipt, setShowFullReceipt] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Auto-redirect to home page after countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [navigate]);

  // Fetch booking data from Firebase or localStorage
  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        setLoading(true);
        
        // First check if we have a booking ID in the URL
        if (booking_id) {
          await fetchBookingById(booking_id);
          return;
        }
        
        // Second, check if we have a payment_intent
        if (payment_intent) {
          await fetchBookingByPaymentIntent(payment_intent);
          return;
        }
        
        // Third, check localStorage for last booking details
        const lastBookingId = localStorage.getItem('lastBookingId');
        const lastBookingDetails = localStorage.getItem('lastBookingDetails');
        
        if (lastBookingId) {
          await fetchBookingById(lastBookingId);
          return;
        }
        
        if (lastBookingDetails) {
          try {
            const parsedDetails = JSON.parse(lastBookingDetails);
            setReceiptData(formatBookingData(parsedDetails));
          } catch (err) {
            console.error("Error parsing booking details from localStorage:", err);
            throw new Error("Unable to retrieve your booking details.");
          }
          return;
        }
        
        // If nothing is found, show an error
        throw new Error("No booking information found. Please contact customer support.");
        
      } catch (err) {
        console.error("Error fetching booking data:", err);
        setError(err.message || "Unable to load booking details. Please contact customer support.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingData();
  }, [booking_id, payment_intent, user]);

  // Fetch booking by ID from Firebase
  const fetchBookingById = async (id) => {
    console.log("Fetching booking with ID:", id);
    
    // Try authenticated user path first
    if (user) {
      try {
        const bookingRef = ref(database, `users/${user.uid}/bookings/${id}`);
        const snapshot = await get(bookingRef);
        
        if (snapshot.exists()) {
          const bookingData = snapshot.val();
          setReceiptData(formatBookingData(bookingData));
          return;
        }
      } catch (err) {
        console.error("Error fetching user booking:", err);
        // Continue to guest booking check
      }
    }
    
    // Try guest booking path
    try {
      const guestBookingRef = ref(database, `guestBookings/${id}`);
      const snapshot = await get(guestBookingRef);
      
      if (snapshot.exists()) {
        const bookingData = snapshot.val();
        setReceiptData(formatBookingData(bookingData));
        return;
      }
      
      throw new Error("Booking not found. Please check your booking reference.");
    } catch (err) {
      console.error("Error fetching guest booking:", err);
      throw err;
    }
  };
  
  // Fetch booking by payment intent
  const fetchBookingByPaymentIntent = async (paymentIntentId) => {
    console.log("Searching for booking with payment intent:", paymentIntentId);
    
    // For authenticated users, search in their bookings
    if (user) {
      try {
        const userBookingsRef = ref(database, `users/${user.uid}/bookings`);
        const snapshot = await get(userBookingsRef);
        
        if (snapshot.exists()) {
          const bookingsData = snapshot.val();
          
          for (const [bookingId, booking] of Object.entries(bookingsData)) {
            if (booking.paymentIntentId === paymentIntentId) {
              setReceiptData(formatBookingData(booking));
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error searching user bookings by payment intent:", err);
        // Continue to guest booking search
      }
    }
    
    // Search in guest bookings
    try {
      const guestBookingsRef = ref(database, 'guestBookings');
      const snapshot = await get(guestBookingsRef);
      
      if (snapshot.exists()) {
        const guestBookingsData = snapshot.val();
        
        for (const [bookingId, booking] of Object.entries(guestBookingsData)) {
          if (booking.paymentIntentId === paymentIntentId) {
            setReceiptData(formatBookingData(booking));
            return;
          }
        }
      }
      
      throw new Error("No booking found for this payment. Please contact support.");
    } catch (err) {
      console.error("Error searching guest bookings by payment intent:", err);
      throw err;
    }
  };
  
  // Format booking data for display
  const formatBookingData = (booking) => {
    // Handle different data structures between authenticated users and guests
    const formattedData = {
      id: booking.id || '',
      date: booking.createdAt || new Date().toISOString(),
      service: {
        title: booking.service?.title || 'Service',
        price: parseFloat(booking.service?.price || 0)
      },
      customer: {
        name: booking.customerName || 'Guest',
        email: booking.guestEmail || booking.contactEmail || 'No email provided'
      },
      vehicle: {
        make: booking.vehicle?.make || '',
        model: booking.vehicle?.model || '',
        year: booking.vehicle?.year || ''
      },
      scheduling: {
        date: booking.schedule?.date || new Date().toISOString(),
        time: booking.schedule?.time || 'Not specified'
      },
      payment: {
        subtotal: parseFloat(booking.payment?.subtotal || 0),
        tax: parseFloat(booking.payment?.tax || 0),
        total: parseFloat(booking.payment?.total || 0),
        last4: booking.payment?.last4 || '****'
      }
    };

    // Send invoice email if we have customer email
    if (formattedData.customer.email && formattedData.customer.email !== 'No email provided') {
      sendInvoiceEmail(formattedData);
    }

    return formattedData;
  };

  // Function to send invoice email
  const sendInvoiceEmail = async (bookingData) => {
    try {
      // Send traditional invoice via Mailgun
      const invoiceData = {
        customerEmail: bookingData.customer.email,
        customerName: bookingData.customer.name,
        invoiceNumber: `INV-${bookingData.id.slice(-6).toUpperCase()}`,
        date: new Date().toISOString(),
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        items: [
          {
            description: bookingData.service.title,
            quantity: 1,
            unitPrice: bookingData.service.price,
            total: bookingData.service.price
          }
        ],
        subtotal: bookingData.payment.subtotal,
        taxRate: 0.0825, // 8.25% tax rate
        tax: bookingData.payment.tax,
        total: bookingData.payment.total,
        paymentStatus: 'Paid'
      };

      await emailService.sendInvoice(invoiceData);
      console.log('Invoice email sent successfully');

      // Also send purchase confirmation via Microsoft Graph
      try {
        const purchaseData = {
          customerEmail: bookingData.customer.email,
          orderId: bookingData.id,
          items: [
            {
              name: bookingData.service.title,
              price: bookingData.service.price,
              quantity: 1
            }
          ],
          subtotal: bookingData.payment.subtotal,
          tax: bookingData.payment.tax,
          total: bookingData.payment.total,
          serviceDate: formatDate(bookingData.scheduling.date),
          serviceTime: bookingData.scheduling.time,
          vehicle: `${bookingData.vehicle.year} ${bookingData.vehicle.make} ${bookingData.vehicle.model}`.trim(),
          paymentMethod: 'Credit Card',
          transactionId: bookingData.id
        };

        await emailService.sendOrderConfirmation(purchaseData);
        console.log('Purchase confirmation email sent via Microsoft Graph');
      } catch (msError) {
        console.error('Error sending purchase confirmation via MS Graph:', msError);
        // Don't fail the overall process if MS Graph fails
      }

    } catch (error) {
      console.error('Error sending invoice email:', error);
      // Don't show error to user, just log it
    }
  };

  // Function to "generate" PDF (we'll simply log for now since we don't have the actual PDF generator)
  const generatePDF = () => {
    if (!receiptData) return;
    
    try {
      setGeneratingPdf(true);
      
      // Placeholder for actual PDF generation
      console.log("Generating PDF for booking:", receiptData.id);
      
      // Simulate PDF generation delay
      setTimeout(() => {
        alert("PDF receipt has been downloaded.");
        setGeneratingPdf(false);
      }, 1500);
      
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("There was a problem generating your receipt. Please try again later.");
      setGeneratingPdf(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (err) {
      console.error("Error formatting date:", err);
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Booking Details</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-all duration-300"
          >
            Return Home <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed!</h1>
          <p className="text-gray-600">
            Thank you for your booking. Your payment has been processed successfully.
          </p>
        </div>
        
        {/* Appointment summary box */}
        <div className="bg-blue-50 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <Calendar className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0" />
            <div>
              <h2 className="font-semibold text-blue-800 text-lg mb-1">Your Appointment Details</h2>
              <p className="text-blue-700 font-medium">
                {receiptData && formatDate(receiptData.scheduling.date)} at {receiptData && receiptData.scheduling.time}
              </p>
              {receiptData && (
                <p className="text-blue-600 mt-1">
                  {receiptData.service.title} for your {receiptData.vehicle.year} {receiptData.vehicle.make} {receiptData.vehicle.model}
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Confirmation email notification */}
        <div className="flex items-start mb-6 bg-green-50 p-4 rounded-lg">
          <Mail className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-green-700">
              A confirmation email has been sent to <span className="font-medium">{receiptData && receiptData.customer.email}</span> with all your booking details.
            </p>
          </div>
        </div>
        
        {/* Booking reference ID */}
        <div className="border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-700 mb-2">Booking Reference</h3>
          <p className="text-sm text-gray-600">
            Your booking ID: <span className="font-mono font-medium">{receiptData?.id || 'Unknown'}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Please save this reference number for future inquiries about your booking.
          </p>
        </div>
        
        {/* Toggle receipt details */}
        <div className="border-t border-gray-200 pt-4 mb-4">
          <button 
            onClick={() => setShowFullReceipt(!showFullReceipt)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <Receipt className="h-5 w-5 text-gray-500 mr-2" />
              <span className="font-medium text-gray-700">Receipt Details</span>
            </div>
            {showFullReceipt ? 
              <ChevronUp className="h-5 w-5 text-gray-500" /> : 
              <ChevronDown className="h-5 w-5 text-gray-500" />
            }
          </button>
        </div>
        
        {/* Receipt details (collapsible) */}
        {showFullReceipt && receiptData && (
          <div className="animate-fadeIn mb-6">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-700">Payment Receipt</h3>
              <button
                onClick={generatePDF}
                disabled={generatingPdf}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingPdf ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-b-2 border-blue-600 rounded-full mr-1"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1" />
                    <span>Download PDF</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Payment Summary */}
            <div className="mb-6">
              <h4 className="text-sm text-gray-500 mb-2">Payment Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{receiptData.service.title}</span>
                  <span>${receiptData.payment.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span>${receiptData.payment.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-medium text-lg pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>${receiptData.payment.total.toFixed(2)}</span>
                </div>
                <div className="text-right text-sm text-gray-500">
                  Paid with card ending in {receiptData.payment.last4}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
          <Link
            to="/"
            className="flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300"
          >
            <Home className="h-5 w-5 mr-2" />
            Return to Home
          </Link>
          <Link
            to="/my-bookings"
            className="flex items-center justify-center bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
          >
            View Appointment <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
        
        {/* Auto-redirect notice */}
        <div className="text-center text-sm text-gray-500 mt-4">
          Redirecting to home page in {countdown} seconds...
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;